// Field specs for the Associate wizard, derived from the "Associate_update" spec.
export const STEP_TITLES = {
  general: "General Information", entity: "Entity Details", photo: "Photo ID Details", comm: "Communication Details",
  bank: "Bank Details", other: "Other Details", license: "License Details", cert: "Certification",
  nominee: "Nominee", commercials: "Commercials",
};
export const PROFESSIONS = ["MFD", "MFD & RIA", "RIA", "CA", "CS", "OTHER"];
export const SELF_PROF = ["MFD", "MFD & RIA", "RIA"];
export const ENTITY_TYPES = ["Individual", "Corporate", "Partnership Firm", "Solo Proprietor"];

export const RX = {
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/, email: /^[^@\s]+@[^@\s]+\.[^@\s]+$/, mobile: /^[6-9]\d{9}$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/, gst: /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, aadhaar: /^[2-9]\d{11}$/,
  tan: /^[A-Z]{4}\d{5}[A-Z]$/, arn: /^(ARN-)?\d{1,7}$/, euin: /^E?\d{6}$/, account: /^\d{9,18}$/,
  phone: /^[0-9+\-\s]{6,15}$/, color: /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/,
};

const f = (key, label, type = "text", req = true, extra = {}) => ({ key, label, type, req, ...extra });
const doc = (key, label, req = true) => f(key, label, "file", req);
const certBlock = (k, label, validityReq = true) => [
  f(`${k}_number`, `${label} CERTIFICATE NUMBER`), doc(`${k}_proof`, "PROOF"), f(`${k}_validity`, "VALIDITY", "date", validityReq, { future: true }),
];

const personBlock = (n, req) => [
  f(`authorised_person_${n}`, `AUTHORISED PERSON ${n}`, "text", req), f(`email_${n}`, `EMAIL ${n}`, "email", req),
  f(`mobile_${n}`, `MOBILE ${n}`, "mobile", req), f(`pan_${n}`, `PAN ${n}`, "pan", req),
];

// ctx = { profile, introducers, isFirst }
export function fieldsFor(step, ctx) {
  const p = ctx.profile || {};
  const g = p.general || {};
  const et = (p.entity || {}).entity_type;
  const prof = g.profession;
  const intro = (ctx.introducers || []).find((i) => i.id === g.introduced_by);
  const indiv = et === "Individual";
  switch (step) {
    case "general": {
      // CA/CS/OTHER need a real introducer (their code is built from the introducer's code).
      const empReq = ["CA", "CS", "OTHER"].includes(prof) && ["Corporate", "Partnership Firm"].includes(intro?.entity_type);
      return [
        f("introduced_by", "INTRODUCED BY", "select", true, {
          options: ctx.isRoot ? [{ value: "self", label: "Self" }]
            : (ctx.introducers || []).map((i) => ({ value: i.id, label: `${i.name} (${i.code})` })),
        }),
        ...(intro?.sales_employees?.length ? [f("introducer_employee", "INTRODUCER-EMPLOYEE NAME", "select", empReq,
          { options: intro.sales_employees.map((e) => ({ value: e.id, label: e.name })) })] : []),
        f("profession", "PROFESSION OF THE INTRODUCEE", "select", true, { options: PROFESSIONS.map((o) => ({ value: o, label: o })) }),
        f("business_tag", "BUSINESS TAG", "text", false, { readOnly: true,
          compute: () => (SELF_PROF.includes(prof) ? "Self" : intro ? intro.name : "") }),
      ];
    }
    case "entity": {
      const base = [f("entity_type", "ENTITY TYPE", "select", true, { options: ENTITY_TYPES.map((o) => ({ value: o, label: o })) })];
      if (et === "Individual") return [...base, f("authorised_person", "AUTHORISED PERSON"), f("email", "EMAIL", "email")];
      if (et === "Corporate" || et === "Partnership Firm")
        return [...base, f("entity_name", "ENTITY NAME (AS PER PANCARD)"), ...personBlock(1, true), ...personBlock(2, true), ...personBlock(3, false)];
      if (et === "Solo Proprietor") return [...base, f("entity_name", "ENTITY NAME (AS PER PANCARD)"), f("mobile", "MOBILE", "mobile"), f("pan", "PAN", "pan")];
      return base;
    }
    case "photo":
      return [
        f("name_as_pan", "NAME (AS PER PANCARD)"), f("pan_number", "PANCARD NUMBER", "pan"), doc("pan_card", "PANCARD"),
        ...(indiv ? [doc("self_photo", "SELF PHOTO")] : []),
        ...(indiv || et === "Solo Proprietor" ? [f("aadhaar_number", "AADHAR CARD NUMBER", "aadhaar"), doc("aadhaar_card", "AADHAR CARD")] : []),
        ...(indiv ? [f("dob", "DATE OF BIRTH", "date", true, { past: true, minAge: 18 })] : [f("doi", "DATE OF INCORPORATION", "date", true, { past: true })]),
      ];
    case "comm":
      return [
        f("address1", "ADDRESS 1"), f("address2", "ADDRESS 2"), f("address3", "ADDRESS 3", "text", false),
        f("_address", "", "address", true), doc("address_proof", "ADDRESS PROOF"), f("mobile", "MOBILE NUMBER", "mobile"),
        f("telephone", "TELEPHONE NO", "phone", false), f("email", "EMAIL ADDRESS", "email"),
      ];
    case "bank": {
      const ria = prof === "MFD & RIA";
      const bankSet = (pre, lbl) => [
        f(`${pre}ifsc`, `${lbl}IFSC CODE`, "ifsc"), doc(`${pre}bank_proof`, `${lbl}BANK PROOF`),
        f(`${pre}bank_name`, `${lbl}BANK NAME`, "text", true, { readOnly: true, lookup: true }), f(`${pre}branch`, `${lbl}BRANCH`, "text", true, { readOnly: true, lookup: true }),
        f(`${pre}micr`, `${lbl}MICR`, "text", false, { readOnly: true }),
        f(`${pre}account_type`, `${lbl}ACCOUNT TYPE`, "select", true, { options: ["Savings", "Current"].map((o) => ({ value: o, label: o })) }),
        f(`${pre}account_number`, `${lbl}ACCOUNT NUMBER`, "account"),
      ];
      return [...bankSet("", ""), ...(ria ? [f("_ria_hdr", "RIA BANK DETAILS", "header", false), ...bankSet("ria_", "RIA ")] : [])];
    }
    case "other": {
      const out = [f("_gst_hdr", "GST DETAILS", "header", false), f("gst_number", "GST NUMBER", "gst", false), doc("gst_proof", "PROOF", !!(p.other || {}).gst_number)];
      if (et === "Solo Proprietor") out.push(f("_se_hdr", "SHOP & ESTABLISHMENT DETAILS", "header", false),
        f("se_certificate_no", "SHOP & ESTABLISHMENT CERTIFICATE NO"), doc("se_proof", "PROOF"), f("se_validity", "VALIDITY", "date"));
      if (et === "Corporate") out.push(f("_corp_hdr", "CORPORATE DETAILS", "header", false),
        doc("moa", "MEMORANDUM OF ASSOCIATION"), doc("aoa", "ARTICLE OF ASSOCIATION"), doc("coi", "CERTIFICATE OF INCORPORATION"),
        doc("signatory_list", "AUTHORIZED SIGNATORY LIST"), doc("board_resolution", "BOARD RESOLUTION"),
        f("tan_number", "TAN NUMBER", "tan", false), doc("tan_upload", "TAN UPLOAD", !!(p.other || {}).tan_number));
      if (et === "Partnership Firm") out.push(f("_pf_hdr", "PARTNERSHIP FIRM DETAILS", "header", false),
        doc("partnership_deed", "PARTNERSHIP DEED"), doc("signatory_list", "AUTHORIZED SIGNATORY LIST"), doc("coi", "CERTIFICATE OF INCORPORATION", false));
      out.push(f("_wl_hdr", "WHITE LABELLING", "header", false), f("primary_color", "PRIMARY COLOR", "color", false),
        doc("logo", "LOGO", false), f("secondary_color", "SECONDARY COLOR", "color", false),
        f("_brand_preview", "BRAND PREVIEW", "brandpreview", false));
      return out;
    }
    case "license": {
      const pre = ["CA", "CS", "OTHER"].includes(prof);
      const base = `${process.env.REACT_APP_BACKEND_URL}/api`;
      // CA/CS/OTHER: ARN proof comes from the Introduced By associate, EUIN from the chosen introducer-employee (view only).
      const emp = pre && (intro?.sales_employees || []).find((e) => e.id === g.introducer_employee);
      const ro = (fileBase) => (pre ? { readOnly: true, fileBase } : {});
      const out = [f("_arn_hdr", "AMFI / ARN DETAILS", "header", false), f("arn_name", "NAME AS PER ARN", "text", true, { readOnly: pre }),
        f("arn_number", "ARN REGISTRATION NUMBER", "arn", true, { readOnly: pre }),
        { ...doc("arn_proof", "ARN PROOF", !pre), ...ro(intro && `${base}/associates/${intro.id}`) },
        f("arn_validity", "VALIDITY", "date", true, { readOnly: pre })];
      if (emp) out.push(f("_euin_hdr", "EUIN DETAILS (INTRODUCER-EMPLOYEE)", "header", false),
        f("euin_name", "NAME OF EUIN HOLDER", "text", false, { readOnly: true }), f("euin_number", "EUIN NUMBER", "euin", false, { readOnly: true }),
        { ...doc("euin_proof", "EUIN PROOF", false), ...ro(`${base}/employees/${emp.id}`) },
        f("euin_validity", "VALIDITY", "date", false, { readOnly: true }));
      else if (indiv) out.push(f("_euin_hdr", "EUIN DETAILS", "header", false), f("euin_name", "NAME OF EUIN HOLDER", "text", true, { readOnly: pre }),
        f("euin_number", "EUIN NUMBER", "euin", true, { readOnly: pre }),
        { ...doc("euin_proof", "EUIN PROOF", !pre), ...ro(intro && `${base}/associates/${intro.id}`) },
        f("euin_validity", "VALIDITY", "date", true, { readOnly: pre }));
      return out;
    }
    case "cert": {
      const c = p.cert || {};
      const ria = (c.ria_types || []);
      const riaBlocks = [f("ria_types", "RIA CERTIFICATION TYPE", "multi", true, { options: ["NISM", "CFP", "CWM"] }),
        ...(ria.includes("NISM") ? [...certBlock("nism_xa", "NISM XA"), ...certBlock("nism_xb", "NISM XB")] : []),
        ...(ria.includes("CFP") ? certBlock("cfp", "CFP") : []), ...(ria.includes("CWM") ? certBlock("cwm", "CWM") : [])];
      if (prof === "CA") return [f("ca_number", "CA CERTIFICATE NO"), doc("ca_proof", "PROOF"), f("ca_validity", "VALIDITY (IF ANY)", "date", false)];
      if (prof === "CS") return [f("cs_number", "CS CERTIFICATE NO"), doc("cs_proof", "PROOF"), f("cs_validity", "VALIDITY (IF ANY)", "date", false)];
      if (prof === "OTHER") return [f("course_name", "NAME OF COURSE/DEGREE"), doc("course_proof", "PROOF"),
        f("course_number", "COURSE CERTIFICATE NO"), f("course_validity", "VALIDITY (IF ANY)", "date", false)];
      if (prof === "MFD") return certBlock("nism_va", "NISM VA");
      if (prof === "RIA") return riaBlocks;
      if (prof === "MFD & RIA") return [f("_mfd_hdr", "MFD CERTIFICATION", "header", false), ...certBlock("nism_va", "NISM VA"),
        f("_ria_hdr", "RIA CERTIFICATION", "header", false), ...riaBlocks];
      return [];
    }
    case "nominee": {
      const n = p.nominee || {};
      const same = !!n.same_address;
      return [f("nominee_name", "NOMINEE NAME"), f("nominee_dob", "DATE OF BIRTH (required if minor)", "date", false, { past: true }),
        f("same_address", "ADDRESS SAME AS PER PRIMARY HOLDER", "checkbox", false),
        ...(same ? [] : [f("address1", "ADDRESS 1"), f("address2", "ADDRESS 2", "text", false), f("address3", "ADDRESS 3", "text", false),
          f("_address", "", "address", true)]),
        f("mobile", "MOBILE NUMBER", "mobile", false), f("telephone", "TELEPHONE NO", "phone", false), f("email", "EMAIL ADDRESS", "email", false),
        ...(isMinor(n.nominee_dob) ? guardianBlock(!!n.guardian_same_address) : [])];
    }
    case "commercials":
      return [f("equity_debt_tax", "Equity, Debt and Tax Investments", "bpspct"), f("arbitrage_cash", "Arbitrage and Cash Investments", "bpspct"),
        f("p2p", "P2P", "bpspct")];
    default:
      return [];
  }
}

const TYPE_RX = { email: "email", mobile: "mobile", pan: "pan", ifsc: "ifsc", gst: "gst", aadhaar: "aadhaar", tan: "tan",
  arn: "arn", euin: "euin", account: "account", phone: "phone", color: "color" };

// Latest birth date allowed for someone who is `age` years old today (YYYY-MM-DD).
export const adultCutoff = (age) => { const d = new Date(); d.setFullYear(d.getFullYear() - age); return d.toISOString().slice(0, 10); };
export const isMinor = (dob) => !!dob && dob > adultCutoff(18);

export const GUARDIAN_RELATIONS = ["Father", "Mother", "Father's father(grandfather)", "Father's mother(grandmother)",
  "Mother's father(materal grandfather)", "Mother's mother(materal grandmother)", "Grand Father", "Grand Mother", "Brother", "Elder Brother",
  "Sister", "Elder Sister", "Uncle", "Aunt", "Father's brother (uncle)", "Father's sister (aunt)", "Mother's brother", "Mother's elder sister (Aunt)",
  "Brother-In-Law", "Sister-In-Law", "Cousin", "Court Appointed Legal Guardian", "Other"];

// Guardian section shown under Nominee Details when the nominee is a minor (mirrors the legacy Guardian Details screen).
const guardianBlock = (same) => [f("_guardian_hdr", "GUARDIAN DETAILS (NOMINEE IS A MINOR)", "header", false),
  f("guardian_name", "GUARDIAN NAME"), f("guardian_pan", "PAN NUMBER", "pan"), doc("guardian_pan_proof", "PAN PROOF"),
  f("guardian_relation", "RELATION WITH NOMINEE", "select", true, { options: GUARDIAN_RELATIONS.map((o) => ({ value: o, label: o })) }),
  f("guardian_same_address", "ADDRESS SAME AS PER PRIMARY HOLDER", "checkbox", false),
  ...(same ? [] : [f("guardian_address1", "ADDRESS 1"), f("guardian_address2", "ADDRESS 2", "text", false), f("guardian_address3", "ADDRESS 3", "text", false),
    f("_guardian_address", "", "address", true, { prefix: "guardian_" })]),
  f("guardian_mobile", "MOBILE NUMBER", "mobile"), f("guardian_telephone", "TELEPHONE NO", "phone", false), f("guardian_email", "EMAIL ADDRESS", "email")];

export function validateStep(step, data, ctx) {
  const errs = {};
  const today = new Date().toISOString().slice(0, 10);
  for (const fl of fieldsFor(step, ctx)) {
    if (fl.type === "header" || fl.type === "brandpreview") continue;
    if (fl.readOnly) {
      if (fl.lookup && fl.req && !data[fl.key]) errs[fl.key] = "Enter a valid IFSC to fill this";
      continue;
    }
    const v = data[fl.key];
    if (fl.type === "address") {
      const x = fl.prefix || "";
      if (!data[`${x}country`]) errs[`${x}country`] = "Required";
      if (data[`${x}has_pincodes`] && !data[`${x}pincode`]) errs[`${x}pincode`] = "Required";
      if (!data[`${x}state`]) errs[`${x}state`] = "Required";
      if (!data[`${x}city`]) errs[`${x}city`] = "Required";
      continue;
    }
    if (fl.type === "bpspct") {
      const b = Number(v?.bps || 0), pc = Number(v?.pct || 0);
      if (!(b > 0) && !(pc > 0)) errs[fl.key] = "Enter BPS or %";
      continue;
    }
    const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length);
    if (empty) { if (fl.req) errs[fl.key] = "Required"; continue; }
    const rx = TYPE_RX[fl.type];
    if (rx && typeof v === "string" && !RX[rx].test(["email", "phone", "color"].includes(rx) ? v.trim() : v.trim().toUpperCase())) errs[fl.key] = `Invalid ${fl.label}`;
    if (fl.type === "date" && (fl.past) && v > today) errs[fl.key] = "Future date not allowed";
    if (fl.minAge && v && v > adultCutoff(fl.minAge)) errs[fl.key] = `Associate must be at least ${fl.minAge} years old`;
  }
  if (step === "license" && data.arn_validity && data.arn_validity < today && !fieldsFor(step, ctx).find((x) => x.key === "arn_validity")?.readOnly)
    errs.arn_validity = errs.arn_validity || "ARN validity has expired";
  return errs;
}
